import React from 'react';
import { Link } from 'react-router-dom';

function DashboardContent() {
    return (
        <div className="bg-[#E2F2FF] rounded-xl p-3 lg:p-3 shadow-md border border-blue-200">
            <div className="max-w-7xl mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
                    {/* Left Column */}
                    <div className="lg:col-span-2 space-y-4 lg:space-y-6">
                        {/* Learning Status */}
                        <div>
                            <h2 className="text-base lg:text-lg font-semibold text-gray-700 mb-3 lg:mb-4">Learning Status</h2>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 lg:gap-4">
                                {/* Completed Modules */}
                                <div className="bg-primary text-white rounded-xl p-5 lg:p-6 text-center shadow-lg border border-blue-600">
                                    <div className="rounded-lg inline-block mb-3">
                                        <svg className="w-10 h-10 lg:w-12 lg:h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                                        </svg>
                                    </div>
                                    <div className="bg-white text-black rounded-lg p-3">
                                        <div className="text-2xl font-bold mb-2 text-gray-700">Completed Modules</div>
                                        <div className="text-4xl lg:text-4xl font-bold">3</div>
                                    </div>
                                </div>
                                {/* Passed Lessons */}
                                <div className="bg-gradient-to-br from-[#52a85e] to-green-700 text-white rounded-xl p-5 lg:p-6 text-center shadow-lg border border-green-600">
                                    <div className="rounded-lg inline-block mb-3">
                                        <svg className="w-10 h-10 lg:w-12 lg:h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                        </svg>
                                    </div>
                                    <div className="bg-white text-black rounded-lg p-3">
                                        <div className="text-2xl font-bold mb-2 text-gray-700">Passed Lessons</div>
                                        <div className="text-4xl lg:text-4xl font-bold">2</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Module Average Scores */}
                        <div>
                            <h2 className="text-base lg:text-lg font-semibold text-gray-700 mb-3 lg:mb-4">Module Average Scores</h2>
                            <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-4 lg:p-6 shadow-md border border-gray-200 overflow-x-auto">
                                <div className="min-w-[600px] lg:min-w-0">
                                    <div className="flex items-end justify-between h-64 lg:h-80 px-2">
                                        {/* Module 1 */}
                                        <div className="flex flex-col items-center flex-1">
                                            <div className="w-full max-w-12 lg:max-w-14 bg-gradient-to-t from-green-600 to-green-400 rounded-t-lg relative shadow-md" style={{height: '81%'}}>
                                                <div className="absolute -top-7 left-1/2 transform -translate-x-1/2 text-xs lg:text-sm font-bold text-gray-800">81%</div>
                                            </div>
                                            <div className="text-xs text-gray-600 mt-3 text-center leading-tight font-medium">Module<br/>1</div>
                                        </div>
                                        {/* Module 2 */}
                                        <div className="flex flex-col items-center flex-1">
                                            <div className="w-full max-w-12 lg:max-w-14 bg-gradient-to-t from-green-500 to-green-300 rounded-t-lg relative shadow-md" style={{height: '69%'}}>
                                                <div className="absolute -top-7 left-1/2 transform -translate-x-1/2 text-xs lg:text-sm font-bold text-gray-800">69%</div>
                                            </div>
                                            <div className="text-xs text-gray-600 mt-3 text-center leading-tight font-medium">Module<br/>2</div>
                                        </div>
                                        {/* Module 3 */}
                                        <div className="flex flex-col items-center flex-1">
                                            <div className="w-full max-w-12 lg:max-w-14 bg-gradient-to-t from-green-600 to-green-400 rounded-t-lg relative shadow-md" style={{height: '91%'}}>
                                                <div className="absolute -top-7 left-1/2 transform -translate-x-1/2 text-xs lg:text-sm font-bold text-gray-800">91%</div>
                                            </div>
                                            <div className="text-xs text-gray-600 mt-3 text-center leading-tight font-medium">Module<br/>3</div>
                                        </div>
                                        {/* Module 4-10 */}
                                        {[4, 5, 6, 7, 8, 9, 10].map(num => (
                                            <div key={num} className="flex flex-col items-center flex-1">
                                                <div className="w-full max-w-12 lg:max-w-14 bg-gray-200 rounded-t-lg relative" style={{height: '5%'}}>
                                                    <div className="absolute -top-7 left-1/2 transform -translate-x-1/2 text-xs lg:text-sm font-bold text-gray-800">0%</div>
                                                </div>
                                                <div className="text-xs text-gray-600 mt-3 text-center leading-tight font-medium">Module<br/>{num}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Column - Module Progress */}
                    <div className="space-y-3 lg:space-y-4">
                        <div className="flex items-center justify-between mb-2 lg:mb-4">
                            <h2 className="text-base lg:text-lg font-semibold text-gray-700">Module Progress</h2>
                        </div>

                        {/* Module 1 */}
                        <div className="bg-primary text-white rounded-xl p-4 flex items-center justify-between shadow-md">
                            <div>
                                <div className="text-xs opacity-80">Module 1</div>
                                <div className="text-sm lg:text-base font-semibold mt-0.5">Understanding Context</div>
                            </div>
                            <div className="w-14 h-14 rounded-full border-4 border-white flex items-center justify-center font-bold text-sm shadow-lg bg-white bg-opacity-10">
                                100%
                            </div>
                        </div>

                        {/* Module 2 */}
                        <div className="bg-primary text-white rounded-xl p-4 flex items-center justify-between shadow-md">
                            <div>
                                <div className="text-xs opacity-80">Module 2</div>
                                <div className="text-sm lg:text-base font-semibold mt-0.5">Themes and Motifs</div>
                            </div>
                            <div className="w-14 h-14 rounded-full border-4 border-white flex items-center justify-center font-bold text-sm shadow-lg bg-white bg-opacity-10">
                                100%
                            </div>
                        </div>

                        {/* Module 3 */}
                        <div className="relative rounded-xl p-4 flex items-center justify-between shadow-md overflow-hidden border border-primary bg-white">
                            <div className="absolute inset-0 bg-primary opacity-90 rounded-xl" style={{width: '70%'}}></div>
                            <div className="relative flex justify-between items-center w-full text-white">
                                <div>
                                    <div className="text-xs opacity-80">Module 3</div>
                                    <div className="text-sm lg:text-base font-semibold mt-0.5">Analyzing Characters</div>
                                </div>
                                <div className="text-primary w-14 h-14 rounded-full border-4 border-gray-300 flex items-center justify-center font-bold text-sm shadow-lg bg-white bg-opacity-10">
                                    70%
                                </div>
                            </div>
                        </div>

                        {/* Modules 4-6 */}
                        {[
                            { id: 4, name: 'Advanced Reading Techniques' },
                            { id: 5, name: 'Critical Thinking in Reading' },
                            { id: 6, name: 'Advanced Data Analysis' }
                        ].map(module => (
                            <div key={module.id} className="bg-white border-2 border-gray-200 rounded-xl p-4 flex items-center justify-between shadow-sm hover:shadow-md transition-shadow">
                                <div>
                                    <div className="text-xs text-gray-600">Module {module.id}</div>
                                    <div className="text-sm lg:text-base font-semibold text-gray-800 mt-0.5">{module.name}</div>
                                </div>
                                <div className="w-14 h-14 rounded-full border-4 border-gray-300 flex items-center justify-center font-bold text-gray-500 text-sm">
                                    0%
                                </div>
                            </div>
                        ))}

                        {/* See All Link */}
                        <div className="text-center pt-2">
                            <Link to="/dashboard/modules" className="text-primary font-semibold text-sm inline-flex items-center gap-2 hover:underline hover:gap-3 transition-all">
                                See All
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3"/>
                                </svg>
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default DashboardContent;
